import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Star,
  MapPin,
  Clock,
  Users,
  ThumbsUp,
  Loader2,
  ArrowLeft,
  Dog,
  Trees,
  Droplets,
  Fence,
  Sun,
  Car,
  Lamp,
  LogIn,
  LogOut,
  CheckCircle,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const amenityDetails = {
  has_fence: { icon: Fence, label: 'Fully Fenced', desc: 'Secure perimeter fencing' },
  has_double_gate: { icon: Fence, label: 'Double Gate Entry', desc: 'Safe entry/exit system' },
  has_water: { icon: Droplets, label: 'Water Available', desc: 'Drinking water for dogs' },
  has_shade: { icon: Sun, label: 'Shaded Areas', desc: 'Trees or structures for shade' },
  has_parking: { icon: Car, label: 'Parking', desc: 'Dedicated parking area' },
  has_lighting: { icon: Lamp, label: 'Lighting', desc: 'Evening visibility' },
  has_small_dog_area: { icon: Dog, label: 'Small Dog Area', desc: 'Separate area for small breeds' },
  has_agility_equipment: { icon: Dog, label: 'Agility Equipment', desc: 'Play structures available' },
  is_off_leash: { icon: Dog, label: 'Off-Leash', desc: 'Dogs can run freely' },
};

const DogParkDetail = () => {
  const { parkId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [park, setPark] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [busyHours, setBusyHours] = useState({});
  const [loading, setLoading] = useState(true);
  const [myDogs, setMyDogs] = useState([]);
  const [myCheckin, setMyCheckin] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [checkinDialogOpen, setCheckinDialogOpen] = useState(false);
  const [selectedDogs, setSelectedDogs] = useState([]);
  
  const [newReview, setNewReview] = useState({
    rating: 5,
    title: '',
    comment: '',
    cleanliness_rating: null,
    safety_rating: null,
    crowdedness: '',
    best_time_to_visit: '',
    pros: [],
    cons: []
  });
  const [prosInput, setProsInput] = useState('');
  const [consInput, setConsInput] = useState('');

  useEffect(() => {
    fetchPark();
    fetchReviews();
    fetchCheckins();
    fetchBusyHours();
    if (user) {
      fetchMyDogs();
      checkMyCheckin();
    }
  }, [parkId, user]);

  const fetchPark = async () => {
    try {
      const response = await axios.get(`${API}/parks/${parkId}`);
      setPark(response.data);
    } catch (error) {
      toast.error('Park not found');
      navigate('/parks');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/parks/${parkId}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const fetchCheckins = async () => {
    try {
      const response = await axios.get(`${API}/parks/${parkId}/checkins?active_only=true`);
      setCheckins(response.data);
    } catch (error) {
      console.error('Error fetching checkins:', error);
    }
  };

  const fetchBusyHours = async () => {
    try {
      const response = await axios.get(`${API}/parks/${parkId}/busy-hours`);
      setBusyHours(response.data);
    } catch (error) {
      console.error('Error fetching busy hours:', error);
    }
  };

  const fetchMyDogs = async () => {
    try {
      const response = await axios.get(`${API}/users/${user.id}/dogs`);
      setMyDogs(response.data);
    } catch (error) {
      console.error('Error fetching dogs:', error);
    }
  };

  const checkMyCheckin = async () => {
    try {
      const response = await axios.get(`${API}/users/me/checkins?active_only=true`);
      const activeCheckin = response.data.find(c => c.park_id === parkId);
      setMyCheckin(activeCheckin || null);
    } catch (error) {
      console.error('Error checking checkin:', error);
    }
  };

  const handleCheckin = async () => {
    try {
      await axios.post(`${API}/parks/${parkId}/checkin`, {
        dog_ids: selectedDogs
      });
      toast.success('Checked in!');
      setCheckinDialogOpen(false);
      setSelectedDogs([]);
      fetchPark();
      fetchCheckins();
      checkMyCheckin();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check in');
    }
  };

  const handleCheckout = async () => {
    try {
      await axios.post(`${API}/parks/${parkId}/checkout`);
      toast.success('Checked out!');
      setMyCheckin(null);
      fetchPark();
      fetchCheckins();
    } catch (error) {
      toast.error('Failed to check out');
    }
  };

  const handleSubmitReview = async () => {
    if (!newReview.comment.trim()) {
      toast.error('Please write a review');
      return;
    }
    
    try {
      const reviewData = {
        ...newReview,
        pros: prosInput.split(',').map(s => s.trim()).filter(Boolean),
        cons: consInput.split(',').map(s => s.trim()).filter(Boolean)
      };
      
      await axios.post(`${API}/parks/${parkId}/reviews`, reviewData);
      toast.success('Review submitted!');
      setReviewDialogOpen(false);
      setNewReview({
        rating: 5, title: '', comment: '',
        cleanliness_rating: null, safety_rating: null,
        crowdedness: '', best_time_to_visit: '', pros: [], cons: []
      });
      setProsInput('');
      setConsInput('');
      fetchPark();
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  const renderStars = (rating, interactive = false, onSelect = null) => (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => interactive && onSelect && onSelect(star)}
          className={interactive ? 'cursor-pointer' : 'cursor-default'}
          disabled={!interactive}
        >
          <Star
            className={`w-5 h-5 ${
              star <= rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'
            } ${interactive ? 'hover:text-amber-400' : ''}`}
          />
        </button>
      ))}
    </div>
  );

  const getBusyLabel = (hour) => {
    const data = busyHours[hour];
    if (!data) return { label: 'No data', color: 'bg-gray-200' };
    
    const colors = {
      'Quiet': 'bg-green-200',
      'Light': 'bg-green-300',
      'Moderate': 'bg-yellow-300',
      'Busy': 'bg-orange-300',
      'Very Busy': 'bg-red-300'
    };
    
    return { label: data.label, color: colors[data.label] || 'bg-gray-200' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (!park) return null;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/parks')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dog Parks
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Card */}
            <Card>
              <CardContent className="p-0">
                {/* Hero Image */}
                <div className="h-64 bg-muted relative">
                  {park.image_url ? (
                    <img 
                      src={park.image_url} 
                      alt={park.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-100 to-green-200">
                      <Trees className="w-24 h-24 text-green-600/50" />
                    </div>
                  )}
                  
                  {park.current_visitors > 0 && (
                    <Badge className="absolute top-4 right-4 bg-green-600 text-lg px-4 py-2">
                      <Users className="w-5 h-5 mr-2" />
                      {park.current_visitors} dogs here now!
                    </Badge>
                  )}
                </div>
                
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h1 className="text-3xl font-heading font-medium">{park.name}</h1>
                      <p className="text-muted-foreground flex items-center gap-1 mt-1">
                        <MapPin className="w-4 h-4" />
                        {park.address && `${park.address}, `}{park.city}{park.state && `, ${park.state}`}
                      </p>
                    </div>
                    {park.is_free ? (
                      <Badge className="bg-blue-600 text-lg">Free</Badge>
                    ) : (
                      <Badge variant="outline" className="text-lg">{park.entry_fee}</Badge>
                    )}
                  </div>

                  {/* Rating */}
                  <div className="flex items-center gap-4 mb-4">
                    {renderStars(park.average_rating)}
                    <span className="text-2xl font-medium">{park.average_rating?.toFixed(1) || '0.0'}</span>
                    <span className="text-muted-foreground">({park.review_count} reviews)</span>
                  </div>

                  {park.description && (
                    <p className="text-muted-foreground mb-6">{park.description}</p>
                  )}

                  {/* Check-in Button */}
                  {user && (
                    <div className="mb-6">
                      {myCheckin ? (
                        <Button 
                          className="bg-red-500 hover:bg-red-600 w-full md:w-auto"
                          onClick={handleCheckout}
                        >
                          <LogOut className="w-5 h-5 mr-2" />
                          Check Out
                        </Button>
                      ) : (
                        <Dialog open={checkinDialogOpen} onOpenChange={setCheckinDialogOpen}>
                          <DialogTrigger asChild>
                            <Button className="bg-green-600 hover:bg-green-700 w-full md:w-auto">
                              <LogIn className="w-5 h-5 mr-2" />
                              Check In Now
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Check in to {park.name}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                              {myDogs.length > 0 && (
                                <div>
                                  <label className="text-sm font-medium mb-2 block">
                                    Which dogs are you bringing?
                                  </label>
                                  <div className="space-y-2">
                                    {myDogs.map((dog) => (
                                      <label key={dog.id} className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-muted">
                                        <input
                                          type="checkbox"
                                          checked={selectedDogs.includes(dog.id)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setSelectedDogs([...selectedDogs, dog.id]);
                                            } else {
                                              setSelectedDogs(selectedDogs.filter(id => id !== dog.id));
                                            }
                                          }}
                                          className="rounded"
                                        />
                                        <Avatar className="w-8 h-8">
                                          <AvatarImage src={dog.image_url} />
                                          <AvatarFallback><Dog className="w-4 h-4" /></AvatarFallback>
                                        </Avatar>
                                        <span>{dog.name}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <Button 
                                className="w-full bg-green-600 hover:bg-green-700" 
                                onClick={handleCheckin}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Check In
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  )}

                  {/* Amenities */}
                  <div>
                    <h3 className="font-heading text-lg mb-3">Amenities</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {Object.entries(amenityDetails).map(([key, { icon: Icon, label, desc }]) => {
                        const hasAmenity = park.amenities?.[key];
                        return (
                          <div 
                            key={key}
                            className={`flex items-center gap-2 p-3 rounded-lg border ${
                              hasAmenity ? 'bg-green-50 border-green-200' : 'bg-muted/50 opacity-50'
                            }`}
                          >
                            <Icon className={`w-5 h-5 ${hasAmenity ? 'text-green-600' : 'text-muted-foreground'}`} />
                            <div>
                              <p className="text-sm font-medium">{label}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Reviews Section */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">Reviews</CardTitle>
                {user && (
                  <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-green-600 hover:bg-green-700">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Write Review
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Review {park.name}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <label className="text-sm font-medium mb-2 block">Overall Rating</label>
                          {renderStars(newReview.rating, true, (rating) => 
                            setNewReview({ ...newReview, rating })
                          )}
                        </div>
                        
                        <Input
                          placeholder="Review Title (optional)"
                          value={newReview.title}
                          onChange={(e) => setNewReview({ ...newReview, title: e.target.value })}
                        />
                        
                        <Textarea
                          placeholder="Share your experience..."
                          value={newReview.comment}
                          onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                          rows={4}
                        />
                        
                        <Input
                          placeholder="Best time to visit (e.g., weekday mornings)"
                          value={newReview.best_time_to_visit}
                          onChange={(e) => setNewReview({ ...newReview, best_time_to_visit: e.target.value })}
                        />
                        
                        <Input
                          placeholder="Pros (comma separated)"
                          value={prosInput}
                          onChange={(e) => setProsInput(e.target.value)}
                        />
                        
                        <Input
                          placeholder="Cons (comma separated)"
                          value={consInput}
                          onChange={(e) => setConsInput(e.target.value)}
                        />
                        
                        <Button 
                          className="w-full bg-green-600 hover:bg-green-700" 
                          onClick={handleSubmitReview}
                        >
                          Submit Review
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent>
                {reviews.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground">No reviews yet. Be the first to review!</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {reviews.map((review) => (
                      <div key={review.id} className="border-b pb-6 last:border-0">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Avatar>
                              <AvatarImage src={review.user_avatar} />
                              <AvatarFallback>{review.username?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{review.username}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
                              </p>
                            </div>
                          </div>
                          {renderStars(review.rating)}
                        </div>
                        
                        {review.title && (
                          <h4 className="font-medium mb-2">{review.title}</h4>
                        )}
                        
                        <p className="text-muted-foreground mb-3">{review.comment}</p>
                        
                        {review.best_time_to_visit && (
                          <p className="text-sm text-green-600 mb-2">
                            <Clock className="w-4 h-4 inline mr-1" />
                            Best time: {review.best_time_to_visit}
                          </p>
                        )}
                        
                        {(review.pros?.length > 0 || review.cons?.length > 0) && (
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            {review.pros?.length > 0 && (
                              <div>
                                <p className="font-medium text-green-600 mb-1">Pros</p>
                                <ul className="list-disc list-inside text-muted-foreground">
                                  {review.pros.map((pro, idx) => <li key={`pro-${pro}`}>{pro}</li>)}
                                </ul>
                              </div>
                            )}
                            {review.cons?.length > 0 && (
                              <div>
                                <p className="font-medium text-red-600 mb-1">Cons</p>
                                <ul className="list-disc list-inside text-muted-foreground">
                                  {review.cons.map((con, idx) => <li key={`con-${con}`}>{con}</li>)}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Who's Here Now */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-600" />
                  Who's Here Now
                </CardTitle>
              </CardHeader>
              <CardContent>
                {checkins.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">
                    No one here yet. Be the first to check in!
                  </p>
                ) : (
                  <div className="space-y-3">
                    {checkins.map((checkin) => (
                      <div key={checkin.id} className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={checkin.user_avatar} />
                          <AvatarFallback>{checkin.username?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-sm">{checkin.username}</p>
                          {checkin.dog_names?.length > 0 && (
                            <p className="text-xs text-muted-foreground">
                              with {checkin.dog_names.join(', ')}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Busy Hours */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Popular Times
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {[6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map((hour) => {
                    const { label, color } = getBusyLabel(hour);
                    const hourLabel = hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`;
                    return (
                      <div key={hour} className="flex items-center gap-2">
                        <span className="text-xs w-12 text-muted-foreground">{hourLabel}</span>
                        <div className="flex-1 h-4 bg-muted rounded overflow-hidden">
                          <div 
                            className={`h-full ${color}`}
                            style={{ width: `${busyHours[hour]?.percentage || 5}%` }}
                          />
                        </div>
                        <span className="text-xs w-16 text-muted-foreground">{label}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Park Info */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Park Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {park.hours_of_operation && (
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Hours</p>
                      <p className="text-sm text-muted-foreground">{park.hours_of_operation}</p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Location</p>
                    <p className="text-sm text-muted-foreground">
                      {park.address && `${park.address}, `}{park.city}{park.state && `, ${park.state}`} {park.zip_code}
                    </p>
                  </div>
                </div>
                
                <div className="pt-3 border-t">
                  <p className="text-sm text-muted-foreground">
                    {park.checkin_count} total check-ins
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DogParkDetail;
